import { getAllClauses, IClause } from "@/api";

import {
    GridColDef,
    GridRowSelectionModel,
    GridSortModel,
} from "@mui/x-data-grid-pro";
import { useQuery } from "@tanstack/react-query";

import { AdminPageHeader, SearchField } from "@/components/admin";
import { DocumentSectionTypesCell } from "@/components/shared/table/cells/DocumentSectionTypesCell";
import { PolicyTypeCell } from "@/components/shared/table/cells/PolicyTypeCell";
import { TopicsCell } from "@/components/shared/table/cells/TopicsCell";
import { Table } from "@/components/shared/table/Table";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { formatDate, handleRowSelection } from "@/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { AddNewClauseButton } from "../AddNewClauseButton";
import { ClauseDetailsButton } from "./ClauseDetailsButton";
import { ClausesExportButton } from "./ClausesExportButton";
import { ClausesImportButton } from "./ClausesImportButton";
import { ClausesTableToolbarActions } from "./ClausesTableToolbarActions";
import { DeleteClauseButton } from "./DeleteClauseButton";

const columns: GridColDef<IClause>[] = [
    {
        field: "id",
        headerName: "ID",
        display: "flex",
    },
    {
        field: "title",
        headerName: "Clause Name",
        display: "flex",
        maxWidth: 300,
        renderCell: ({ row }) => <ClauseDetailsButton clause={row} />,
    },
    {
        field: "policy_type",
        headerName: "Policy Type",
        display: "flex",
        renderCell: ({ row }) => (
            <PolicyTypeCell policyType={row.policy_type} />
        ),
    },
    {
        field: "document_section_types",
        headerName: "Section Types",
        display: "flex",
        maxWidth: 300,
        sortable: false,
        renderCell: ({ row }) => (
            <DocumentSectionTypesCell
                documentSectionTypes={row.document_section_types}
            />
        ),
    },
    {
        field: "topics",
        headerName: "Topics",
        display: "flex",
        maxWidth: 300,
        sortable: false,
        renderCell: ({ row }) => <TopicsCell topics={row.topics} />,
    },
    {
        field: "updated_at",
        headerName: "Last Updated",
        type: "date",
        display: "flex",
        valueFormatter: (value) => {
            return formatDate(value);
        },
    },
    {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        getActions: ({ row }) => [<DeleteClauseButton clauseId={row.id} />],
    },
];

/**
 * Table displaying clauses and actions
 */
export const ClausesTable = () => {
    const [selectedClauseIds, setSelectedClauseIds] = useState<number[]>([]);

    const handleRowSelectionModelChange = (
        rowSelectionModel: GridRowSelectionModel
    ) => {
        setSelectedClauseIds(handleRowSelection(rowSelectionModel, rows));
    };

    const [rows, setRows] = useState<IClause[]>([]);

    const {
        search,
        debouncedSearch,
        setSearch,
        clear: handleSearchClear,
    } = useDebouncedSearch();

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    const [sortModel, setSortModel] = useState<GridSortModel>([
        { field: "id", sort: "desc" },
    ]);

    useEffect(() => {
        setPaginationModel((prev) =>
            prev.page === 0 ? prev : { ...prev, page: 0 }
        );
    }, [debouncedSearch, sortModel]);

    const sortFilter =
        sortModel.length > 0
            ? { sort_by: sortModel[0].field, order: sortModel[0].sort ?? "asc" }
            : {};

    const autoSizeOptions = {
        columns: [
            "id",
            "policy_type",
            "document_section_types",
            "topics",
            "title",
            "updated_at",
            "actions",
        ],
        includeHeaders: true,
        includeOutliers: true,
        expand: true,
    };

    // Fetch clauses from the API
    const { data, isFetching } = useQuery({
        queryKey: [
            "admin-clauses",
            paginationModel.page,
            paginationModel.pageSize,
            debouncedSearch,
            sortModel,
        ],
        queryFn: () =>
            getAllClauses(
                paginationModel.page + 1,
                paginationModel.pageSize,
                sortFilter,
                undefined,
                debouncedSearch || undefined
            ),
    });

    useEffect(() => {
        if (data) {
            setRows(data.items);
        }
    }, [data]);

    // memoize rowCount to avoid resetting the page to 0 when the data is loading
    const rowCountRef = useRef(data?.total || 0);

    const rowCount = useMemo(() => {
        if (data?.total !== undefined) {
            rowCountRef.current = data?.total;
        }
        return rowCountRef.current;
    }, [data?.total]);

    return (
        <>
            <AdminPageHeader
                title="Clauses"
                subtitle="Define and import clauses used in scope analysis"
            >
                <ClausesImportButton />
                <ClausesExportButton />
                <AddNewClauseButton />
                <ClausesTableToolbarActions
                    selectedClauseIds={selectedClauseIds}
                />
            </AdminPageHeader>
            <div className="mt-6">
                <div className="mb-4">
                    <SearchField
                        value={search}
                        onChange={setSearch}
                        onClear={handleSearchClear}
                        placeholder="Search clauses..."
                    />
                </div>
                <Table
                    loading={isFetching}
                    columns={columns}
                    rows={rows}
                    checkboxSelection
                    onRowSelectionModelChange={handleRowSelectionModelChange}
                    disableColumnResize
                    disableColumnFilter
                    autosizeOptions={autoSizeOptions}
                    autoSize
                    pageSizeOptions={[10, 25, 50]}
                    paginationModel={paginationModel}
                    rowCount={rowCount}
                    onPaginationModelChange={setPaginationModel}
                    paginationMode={"server"}
                    sortingMode={"server"}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                />
            </div>
        </>
    );
};
